import { dates } from "@omegagrid/core";
import { CellSource } from "@omegagrid/grid";
import { FormControlProperties, FormControlType, FormValues } from "@omegagrid/form";
import { msg } from "@omegagrid/localize";
import { EXTENDED_OCCURRENCE_TYPES, WEEK_OCCURRENCE_TYPES } from "@reporting/rp-types/jobs";
import { formatDate as formatLocaleDate, formatDateTime as formatLocaleDateTime } from "./formatters";

/**
 * The schedule columns shared by the scheduled report e-mails (P_REPORT_MAIL) and the server
 * tasks (P_TASK): the SQL Agent convention of dbo.f_GET_NEXT_SCHEDULE. freq_type is one of
 * EXTENDED_OCCURRENCE_TYPES; freq_interval is a week-day bitmask (weekly), a day of month (monthly)
 * or a month bitmask (yearly); the time of day of every run comes from start_date.
 *
 * The tasks alone repeat within the day (DPL_TASK.FREQ_SUBDAY_TYPE / _INTERVAL, the super db procedure
 * p_GET_NEXT_SCHEDULE): every freq_subday_interval minutes or hours from the time of
 * start_date until midnight on every scheduled day; null = once a day.
 */
export type Scheduled = {
	freq_type?: number;
	freq_interval?: number;
	freq_subday_type?: number;
	freq_subday_interval?: number;
	start_date?: string;
	end_date?: string;
	last_run?: string;
	next_run?: string;
}

export type ScheduleValues = Pick<Scheduled, 'freq_type' | 'freq_interval' | 'freq_subday_type' | 'freq_subday_interval' | 'start_date' | 'end_date'>;

/** FREQ_SUBDAY_TYPE of a task (the SQL Agent convention): how the run repeats within a scheduled day. */
export const SUBDAY_OCCURRENCE_TYPES = {
	once: { id: 1, text: 'Once a day' },
	minutes: { id: 4, text: 'Every X minutes' },
	hours: { id: 8, text: 'Every X hours' },
} as const;

/** The sub-day repetition of a task as text ("every 15 minutes"), null when it runs once a day. */
const subdayText = (item: Scheduled): string | null => {
	const n = item.freq_subday_interval;
	if (!(n > 0)) return null;
	switch (item.freq_subday_type) {
		case SUBDAY_OCCURRENCE_TYPES.minutes.id: return msg('every {n} minutes', { data: { n } });
		case SUBDAY_OCCURRENCE_TYPES.hours.id: return msg('every {n} hours', { data: { n } });
		default: return null;
	}
};

// the user's date formats (the ones the dt / d grid columns use), null for an empty value
const formatDate = (d?: string) => d ? formatLocaleDateTime(d) : null;
const formatDay = (d?: string) => d ? formatLocaleDate(d) : null;
const formatTime = (d?: string) => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : null;

/** The month numbers set in a yearly bitmask (bit m-1 = month m). */
const monthsOf = (mask: number) => Array.from({ length: 12 }, (_, i) => i + 1).filter(m => (mask & (1 << (m - 1))) === (1 << (m - 1)));

/** The frequencies whose freq_interval picks the days (or months) and whose validity period matters. */
const PERIODIC_TYPES: number[] = [EXTENDED_OCCURRENCE_TYPES.weekly.id, EXTENDED_OCCURRENCE_TYPES.monthly.id, EXTENDED_OCCURRENCE_TYPES.yearly.id];

/**
 * The rule as one line of text: the frequency, the week days / day of month / months it picks and
 * the time of day with the sub-day repetition, e.g. "Weekly: Monday, Wednesday (08:00, every 15 minutes)";
 * a one-off shows its date and time.
 */
export function createScheduleText(item: Scheduled): string {
	// the first run of the day, with the repetition that follows it
	const firstRun = formatTime(item.start_date);
	const subday = subdayText(item);
	const time = firstRun && subday ? `${firstRun}, ${subday}` : firstRun;
	const int = item.freq_interval;
	const withTime = (text: string) => time ? `${text} (${time})` : text;

	switch (item.freq_type) {
		case EXTENDED_OCCURRENCE_TYPES.once.id:
			return `${msg(EXTENDED_OCCURRENCE_TYPES.once.text)}: ${formatDate(item.start_date) ?? '-'}`;
		case EXTENDED_OCCURRENCE_TYPES.daily.id:
			return `${msg(EXTENDED_OCCURRENCE_TYPES.daily.text)}: ${time ?? '-'}`;
		case EXTENDED_OCCURRENCE_TYPES.weekly.id: {
			const days = Object.values(WEEK_OCCURRENCE_TYPES)
				.filter(v => (int & v.id) === v.id)
				.map(v => msg(v.text));
			return withTime(`${msg(EXTENDED_OCCURRENCE_TYPES.weekly.text)}: ${days.join(', ')}`);
		}
		case EXTENDED_OCCURRENCE_TYPES.monthly.id:
			return withTime(`${msg(EXTENDED_OCCURRENCE_TYPES.monthly.text)}: ${int > 0 && int < 32 ? `${int}.` : '-'}`);
		case EXTENDED_OCCURRENCE_TYPES.yearly.id:
			return withTime(`${msg(EXTENDED_OCCURRENCE_TYPES.yearly.text)}: ${monthsOf(int).join(', ')}`);
		default:
			return '';
	}
}

/** The validity period as "from – to" (days only, ∞ for no end); empty for a one-off or without a start date. */
export function createSchedulePeriodText(item: Scheduled): string {
	const from = formatDay(item.start_date);
	return from && item.freq_type !== EXTENDED_OCCURRENCE_TYPES.once.id ? `${from} – ${formatDay(item.end_date) ?? '∞'}` : '';
}

/**
 * One HTML cell describing the schedule: the rule (createScheduleText), the validity period of a
 * periodic one, the last run and the next run (a stop icon when there is none).
 */
export function createScheduleHtml(item: Scheduled): Partial<CellSource> {
	let html = `<div>${createScheduleText(item)}`;

	const from = formatDate(item.start_date);
	if (from && PERIODIC_TYPES.includes(item.freq_type)) {
		html += `<br><strong>${msg('From date')}:</strong> ${from}`;
		html += ` <strong>${msg('To date')}:</strong> ${formatDate(item.end_date) ?? '&infin;'}`;
	}

	if (item.last_run) {
		html += `<br /><strong>${msg('Last run')}:</strong> ${formatDate(item.last_run)}`;
	}

	html += `<br /><strong>${msg('Next run')}:</strong> `;
	html += item.next_run
		? formatDate(item.next_run)
		: '<og-icon icon="circle-stop"></og-icon>';

	html += '</div>';
	return { t: 'h', v: html };
}

/** The sub-day controls of createScheduleControls: offered only to the tasks. */
export type SubdayScheduleOptions = {
	/** the FREQ_SUBDAY_TYPE currently chosen in the form (null = once a day) */
	type: number | null;
};

/**
 * The form controls of a schedule, starting at the given row: the frequency list, the interval
 * control the chosen frequency needs (week days / day of month / months, under the keys
 * `_freq_interval_weekly|monthly|yearly` that encodeSchedule reads back), with `subday` the
 * repetition within the day (`freq_subday_type` + `freq_subday_interval`, not for a one-off), and
 * the validity period: the start date carries the time of day of the runs, so it is a date-time
 * picker; the end date is a day. Re-create the controls when the frequency or the sub-day type
 * changes (see ReportEmailFormAdapter / TaskFormAdapter.updateFormControls).
 *
 * @param item the edited record, null for a new one
 * @param frequencyType the frequency currently chosen in the form
 * @param row the first row to use
 * @param cs column span of the inputs (the label sits in column 0)
 * @param subday offer the sub-day repetition, with the type currently chosen in the form
 * @returns the controls and the next free row
 */
export function createScheduleControls(item: Scheduled | null, frequencyType: number, row: number, cs = 2, subday?: SubdayScheduleOptions): { controls: FormControlProperties[], row: number } {
	let r = row;
	const controls: FormControlProperties[] = [
		{row: r, col: 0, type: FormControlType.Label, value: msg('Frequency'), for: 'freq_type'},
		{row: r++, col: 1, cs, type: FormControlType.List, value: item?.freq_type || EXTENDED_OCCURRENCE_TYPES.once.id, key: 'freq_type', content: {
			sourceData: Object.entries(EXTENDED_OCCURRENCE_TYPES).map(([k, o]) => ({ k: o.id, v: msg(o.text) })),
			options: { selectMode: 'single', autoClose: true }
		}},
	];

	// the stored interval is only meaningful for the frequency it was stored with
	const stored = item?.freq_type === frequencyType ? item?.freq_interval : null;

	if (frequencyType === EXTENDED_OCCURRENCE_TYPES.weekly.id) {
		const selectedWeekDays = stored != null
			? Object.values(WEEK_OCCURRENCE_TYPES).filter(v => (stored & v.id) === v.id).map(v => [v.id, 0, 1])
			: null;
		controls.push(
			{row: r, col: 0, type: FormControlType.Label, value: msg('Week day'), for: '_freq_interval_weekly'},
			{row: r++, col: 1, cs, type: FormControlType.List, key: '_freq_interval_weekly', value: selectedWeekDays, required: true, content: {
				sourceData: Object.entries(WEEK_OCCURRENCE_TYPES).map(([k, o]) => ({ k: o.id, v: msg(o.text) })),
				options: { selectMode: 'multiple', autoClose: false }
			}}
		);
	} else if (frequencyType === EXTENDED_OCCURRENCE_TYPES.monthly.id) {
		controls.push(
			{row: r, col: 0, type: FormControlType.Label, value: msg('Month day'), for: '_freq_interval_monthly'},
			{row: r++, col: 1, cs, type: FormControlType.List, key: '_freq_interval_monthly', value: stored, required: true, content: {
				sourceData: Array.from({ length: 31 }, (_, i) => ({ k: i + 1, v: (i + 1).toString() })),
				options: { selectMode: 'single', autoClose: true }
			}}
		);
	} else if (frequencyType === EXTENDED_OCCURRENCE_TYPES.yearly.id) {
		const selectedMonths = stored != null ? monthsOf(stored).map(v => [v, 0, 1]) : null;
		controls.push(
			{row: r, col: 0, type: FormControlType.Label, value: msg('Month'), for: '_freq_interval_yearly'},
			{row: r++, col: 1, cs, type: FormControlType.List, key: '_freq_interval_yearly', value: selectedMonths, required: true, content: {
				sourceData: Array.from({ length: 12 }, (_, i) => ({ k: i + 1, v: (i + 1).toString() })),
				options: { selectMode: 'multiple', autoClose: false }
			}}
		);
	}

	if (subday && frequencyType !== EXTENDED_OCCURRENCE_TYPES.once.id) {
		const subdayType = subday.type || SUBDAY_OCCURRENCE_TYPES.once.id;
		controls.push(
			{row: r, col: 0, type: FormControlType.Label, value: msg('Repeat'), for: 'freq_subday_type'},
			{row: r++, col: 1, cs, type: FormControlType.List, key: 'freq_subday_type', value: subdayType, content: {
				sourceData: Object.values(SUBDAY_OCCURRENCE_TYPES).map(o => ({ k: o.id, v: msg(o.text) })),
				options: { selectMode: 'single', autoClose: true }
			}},
		);
		if (subdayType !== SUBDAY_OCCURRENCE_TYPES.once.id) {
			// the stored interval is only meaningful for the sub-day type it was stored with
			const storedInterval = item?.freq_subday_type === subdayType ? item?.freq_subday_interval : null;
			controls.push(
				{row: r, col: 0, type: FormControlType.Label, value: msg('Interval'), for: 'freq_subday_interval'},
				{row: r++, col: 1, cs, type: FormControlType.NumericInput, key: 'freq_subday_interval', value: storedInterval ?? null, required: true,
					placeholder: msg(subdayType === SUBDAY_OCCURRENCE_TYPES.minutes.id ? 'Minutes' : 'Hours')},
			);
		}
	}

	controls.push(
		{row: r, col: 0, type: FormControlType.Label, value: msg('From date'), for: 'start_date'},
		{row: r++, col: 1, cs, type: FormControlType.DateTimeInput, key: 'start_date', value: item?.start_date || null, required: true},
		{row: r, col: 0, type: FormControlType.Label, value: msg('To date'), for: 'end_date'},
		{row: r++, col: 1, cs, type: FormControlType.CalendarDateInput, key: 'end_date', value: item?.end_date || null},
	);
	return { controls, row: r };
}

/**
 * A calendar control value as the SQL date-time text the api takes, in the user's local time: the
 * picker gives a Date, an untouched control keeps the string it was given. (JSON would send a Date
 * as UTC, shifting the time of day by the time zone offset.)
 */
const toDateTimeText = (value: unknown): string | null => {
	if (value instanceof Date) return isNaN(value.getTime()) ? null : dates.format(value, 'yyyy-MM-dd HH:mm:ss');
	if (typeof value === 'string') return value.trim() || null;
	return null;
};

/**
 * The schedule columns from the values of a form built by createScheduleControls: the interval
 * control of the chosen frequency encoded back into freq_interval (bitmasks for week days and
 * months); a frequency without an interval control leaves freq_interval out. The dates are sent
 * as local date-time text. The sub-day columns are set only when the form had the sub-day
 * controls: null for once a day, so a repetition edited away is cleared.
 */
export function encodeSchedule(values: FormValues): ScheduleValues {
	const schedule: ScheduleValues = {
		freq_type: values.freq_type as number,
		start_date: toDateTimeText(values.start_date),
		end_date: toDateTimeText(values.end_date),
	};
	if (values.freq_type === EXTENDED_OCCURRENCE_TYPES.weekly.id) {
		const weekDays = (values._freq_interval_weekly as any[][] || []).map(([first]) => first);
		schedule.freq_interval = weekDays.reduce((acc, v) => acc | v, 0);
	} else if (values.freq_type === EXTENDED_OCCURRENCE_TYPES.monthly.id) {
		schedule.freq_interval = values._freq_interval_monthly as number;
	} else if (values.freq_type === EXTENDED_OCCURRENCE_TYPES.yearly.id) {
		const months = (values._freq_interval_yearly as any[][] || []).map(([first]) => first);
		schedule.freq_interval = months.reduce((acc, v) => acc | (1 << (v - 1)), 0);
	}
	if ('freq_subday_type' in values) {
		const subdayType = values.freq_subday_type as number;
		const repeats = subdayType === SUBDAY_OCCURRENCE_TYPES.minutes.id || subdayType === SUBDAY_OCCURRENCE_TYPES.hours.id;
		schedule.freq_subday_type = repeats ? subdayType : null;
		schedule.freq_subday_interval = repeats ? Number(values.freq_subday_interval) || null : null;
	}
	return schedule;
}
